import { useState, useCallback } from 'react';
import {
  isWebUSBAvailable,
  connectPrinter,
  disconnectPrinter,
  getPrinterStatus,
} from '../../services/receiptPrinter';

/**
 * Map paper width (mm) to default characters per line.
 */
const WIDTH_TO_CHARS = {
  58: 32,
  72: 40,
  80: 48,
};

const PAPER_WIDTH_OPTIONS = [
  { value: 58, label: '58 mm' },
  { value: 72, label: '72 mm' },
  { value: 80, label: '80 mm' },
];

/**
 * Settings modal for the POS system.
 * Contains Receipt Printer and Invoice configuration sections.
 *
 * @param {Object}   props
 * @param {Function} props.onClose   - Close the modal
 * @param {Object}   props.settings  - Current settings object
 * @param {Function} props.onSave    - Called with updated settings on save
 */
export default function Settings({ onClose, settings, onSave }) {
  // Clone settings into local state
  const [local, setLocal] = useState(() => ({
    receipt_width_mm: settings?.receipt_width_mm ?? 80,
    receipt_chars_per_line: settings?.receipt_chars_per_line ?? 48,
    auto_print_receipt: settings?.auto_print_receipt ?? false,
    invoice_logo_url: settings?.invoice_logo_url ?? '',
    invoice_logo_id: settings?.invoice_logo_id ?? 0,
    crm_url: settings?.crm_url ?? '',
    crm_api_key: settings?.crm_api_key ?? '',
  }));

  // Printer state
  const [printerStatus, setPrinterStatus] = useState(getPrinterStatus());
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Hidden file input ref for non-WP contexts
  const [fileInputKey, setFileInputKey] = useState(0);

  // ---- Printer handlers ----

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      await connectPrinter();
      setPrinterStatus(getPrinterStatus());
    } catch (err) {
      setError(err.message || 'Failed to connect printer');
    } finally {
      setConnecting(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    await disconnectPrinter();
    setPrinterStatus(getPrinterStatus());
  }, []);

  // ---- Settings change handlers ----

  const handleWidthChange = useCallback((e) => {
    const width = Number(e.target.value);
    setLocal((prev) => ({
      ...prev,
      receipt_width_mm: width,
      receipt_chars_per_line: WIDTH_TO_CHARS[width] ?? prev.receipt_chars_per_line,
    }));
  }, []);

  const handleCharsChange = useCallback((e) => {
    const chars = Number(e.target.value);
    if (chars > 0) {
      setLocal((prev) => ({ ...prev, receipt_chars_per_line: chars }));
    }
  }, []);

  const handleAutoPrintToggle = useCallback(() => {
    setLocal((prev) => ({ ...prev, auto_print_receipt: !prev.auto_print_receipt }));
  }, []);

  // ---- Logo handlers ----

  const handleLogoUpload = useCallback(() => {
    // WordPress admin context — use the WP media picker
    if (window.wp?.media) {
      const frame = window.wp.media({
        title: 'Select Invoice Logo',
        button: { text: 'Use as Logo' },
        multiple: false,
        library: { type: 'image' },
      });

      frame.on('select', () => {
        const attachment = frame.state().get('selection').first().toJSON();
        setLocal((prev) => ({
          ...prev,
          invoice_logo_url: attachment.url,
          invoice_logo_id: attachment.id,
        }));
      });

      frame.open();
      return;
    }

    // Standalone / preview context — trigger hidden file input
    setFileInputKey((k) => k + 1);
    setTimeout(() => {
      const input = document.getElementById('settings-logo-file-input');
      if (input) input.click();
    }, 0);
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setLocal((prev) => ({
      ...prev,
      invoice_logo_url: url,
      invoice_logo_id: 0,
    }));
  }, []);

  const handleRemoveLogo = useCallback(() => {
    setLocal((prev) => ({
      ...prev,
      invoice_logo_url: '',
      invoice_logo_id: 0,
    }));
  }, []);

  // ---- Modal actions ----

  const handleSave = useCallback(() => {
    onSave(local);
  }, [local, onSave]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
    >
      <div className="bg-white rounded-xl shadow-pos-modal w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-pos-border">
          <h2 className="text-lg font-semibold text-pos-foreground">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-pos-muted-fg hover:text-pos-foreground transition-colors p-1 rounded-lg hover:bg-pos-muted"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ---- Scrollable body ---- */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* ======== Receipt Printer Section ======== */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              {/* Receipt printer icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-pos-primary"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              <h3 className="text-sm font-semibold text-pos-foreground uppercase tracking-wide">
                Receipt Printer
              </h3>
            </div>

            <div className="space-y-3">
              {/* WebUSB status */}
              <div className="flex items-center gap-3 p-3 bg-pos-muted/50 rounded-lg">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    printerStatus.webUSBAvailable ? 'bg-pos-success' : 'bg-pos-danger'
                  }`}
                />
                <div>
                  <div className="text-sm font-medium text-pos-foreground">WebUSB Support</div>
                  <div className="text-xs text-pos-muted-fg">
                    {printerStatus.webUSBAvailable
                      ? 'Available \u2014 USB thermal printers supported'
                      : 'Not available \u2014 use Chrome or Edge for USB printing'}
                  </div>
                </div>
              </div>

              {/* Printer connection */}
              <div className="flex items-center gap-3 p-3 bg-pos-muted/50 rounded-lg">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    printerStatus.connected ? 'bg-pos-success' : 'bg-pos-border'
                  }`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-pos-foreground">Receipt Printer</div>
                  <div className="text-xs text-pos-muted-fg">
                    {printerStatus.connected ? 'Connected' : 'Not connected'}
                  </div>
                </div>
                {printerStatus.connected ? (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    className="px-3 py-1.5 text-sm border border-pos-border rounded-lg text-pos-muted-fg hover:bg-pos-muted transition-colors"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleConnect}
                    disabled={!printerStatus.webUSBAvailable || connecting}
                    className="px-3 py-1.5 text-sm bg-pos-primary text-white rounded-lg hover:bg-pos-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {connecting ? 'Connecting\u2026' : 'Connect'}
                  </button>
                )}
              </div>

              {/* Printer error */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Paper width */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="settings-paper-width"
                    className="block text-sm font-medium text-pos-foreground mb-1"
                  >
                    Paper Width
                  </label>
                  <select
                    id="settings-paper-width"
                    value={local.receipt_width_mm}
                    onChange={handleWidthChange}
                    className="w-full rounded-lg border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                  >
                    {PAPER_WIDTH_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Characters per line */}
                <div>
                  <label
                    htmlFor="settings-chars-per-line"
                    className="block text-sm font-medium text-pos-foreground mb-1"
                  >
                    Chars Per Line
                  </label>
                  <input
                    id="settings-chars-per-line"
                    type="number"
                    min="1"
                    max="120"
                    value={local.receipt_chars_per_line}
                    onChange={handleCharsChange}
                    className="w-full rounded-lg border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                  />
                </div>
              </div>

              {/* Auto-print toggle */}
              <label
                htmlFor="settings-auto-print"
                className="flex items-center gap-3 p-3 bg-pos-muted/50 rounded-lg cursor-pointer select-none"
              >
                <input
                  id="settings-auto-print"
                  type="checkbox"
                  checked={local.auto_print_receipt}
                  onChange={handleAutoPrintToggle}
                  className="w-4 h-4 rounded border-pos-border text-pos-primary focus:ring-pos-primary/30 accent-pos-primary"
                />
                <div>
                  <div className="text-sm font-medium text-pos-foreground">Auto-print Receipt</div>
                  <div className="text-xs text-pos-muted-fg">
                    Automatically print a receipt after each completed sale
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* ======== Invoice Section ======== */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              {/* Document icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-pos-navy"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
              <h3 className="text-sm font-semibold text-pos-foreground uppercase tracking-wide">
                Invoice
              </h3>
            </div>

            <div className="space-y-3">
              {/* Logo upload */}
              <div>
                <label className="block text-sm font-medium text-pos-foreground mb-1">
                  Invoice Logo
                </label>

                {local.invoice_logo_url ? (
                  /* Logo preview */
                  <div className="flex items-center gap-4 p-3 border border-pos-border rounded-lg bg-pos-muted/30">
                    <img
                      src={local.invoice_logo_url}
                      alt="Invoice logo"
                      className="max-h-[60px] w-auto object-contain rounded"
                    />
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="px-3 py-1.5 text-sm text-pos-danger border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  /* Upload area */
                  <button
                    type="button"
                    onClick={handleLogoUpload}
                    className="w-full flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-pos-border rounded-lg text-pos-muted-fg hover:border-pos-primary hover:text-pos-primary transition-colors cursor-pointer bg-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-6 h-6"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span className="text-sm font-medium">Upload Logo</span>
                  </button>
                )}

                {/* Hidden file input for non-WP contexts */}
                <input
                  key={fileInputKey}
                  id="settings-logo-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                <p className="text-xs text-pos-muted-fg mt-2">
                  Logo will appear at the top of printed invoices
                </p>
              </div>
            </div>
          </div>

          {/* ======== CRM Integration Section ======== */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-pos-primary"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h3 className="text-sm font-semibold text-pos-foreground uppercase tracking-wide">
                Symple Hub
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor="settings-crm-url"
                  className="block text-sm font-medium text-pos-foreground mb-1"
                >
                  Symple Hub URL
                </label>
                <input
                  id="settings-crm-url"
                  type="url"
                  placeholder="https://hub.symplehub.com"
                  value={local.crm_url}
                  onChange={(e) => setLocal((prev) => ({ ...prev, crm_url: e.target.value }))}
                  className="w-full rounded-lg border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground placeholder:text-pos-muted-fg/50 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor="settings-crm-secret"
                  className="block text-sm font-medium text-pos-foreground mb-1"
                >
                  API Key
                </label>
                <input
                  id="settings-crm-secret"
                  type="password"
                  placeholder="Enter API key"
                  value={local.crm_api_key}
                  onChange={(e) => setLocal((prev) => ({ ...prev, crm_api_key: e.target.value }))}
                  className="w-full rounded-lg border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground placeholder:text-pos-muted-fg/50 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                />
                <p className="text-xs text-pos-muted-fg mt-1">
                  Used to authenticate requests to Symple Hub
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Footer ---- */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-pos-border bg-pos-muted/30">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-pos-border bg-white text-sm font-medium text-pos-foreground hover:bg-pos-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 rounded-lg bg-pos-primary text-white text-sm font-semibold hover:bg-pos-primary-dark transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
